import { includes, isEmpty, map } from "lodash";
import classNames from "classnames";
import {
  faCalendarPlus,
  faFont,
  faListCheck,
  faQuestion,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormContext } from "react-hook-form";
import { stringify } from "query-string";
import { useActivitystreams } from "../../../api/activitystreams";
import { openQuestionnaireEditor } from "../../questionnaires/editor/handover";
import { defaultTargetStream } from "./StreamTargeting";

type TypeSelectorArgs = {
  selectedType: string;
  setType: (type: string) => void;
  currentStream?: { slug: string; name: string } | null;
  // a closed composer has no visibility selection yet
  open?: boolean;
};

export default function TypeSelector({
  selectedType,
  setType,
  currentStream,
  open = false,
}: TypeSelectorArgs) {
  const { watch } = useFormContext();
  const { data: activitystreams } = useActivitystreams();
  const ICON_LOOKUP = {
    note: faFont,
    question: faQuestion,
    appointment: faCalendarPlus,
    questionnaire: faListCheck,
  };
  const selectableTypes = ["note", "question"];
  const streams = watch("activitystreams");
  const content = watch("object.content");
  const contentTranslations = watch("object.translations");

  if (includes(Preload.current_network.availableModules, "Calendars"))
    selectableTypes.push("appointment");
  selectableTypes.push("questionnaire");

  function createAppointment() {
    window.InterceptorPrompt.allowNavigation();

    const params = {
      post_to_stream: true,
    };

    if (!isEmpty(streams)) params["streams"] = map(streams, "value").join(",");

    if (!isEmpty(content)) params["description"] = content;

    // Workaround to disable interceptor for appointment creation
    window.location.href = `/appointments/new?${stringify(params)}`;
  }

  // From the closed composer the questionnaire gets the stream the open one would preselect
  function createQuestionnaire() {
    const target = open
      ? undefined
      : defaultTargetStream(activitystreams, currentStream);
    openQuestionnaireEditor(
      target ? [{ value: target.slug }] : streams,
      content,
      contentTranslations,
    );
  }

  return (
    <div className="type-selector flex gap-2 items-center">
      {map(selectableTypes, (type) => (
        <button
          key={type}
          className={classNames("btn btn-light btn-sm", {
            active: type === selectedType,
          })}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            if (type === "appointment") createAppointment();
            else if (type === "questionnaire") createQuestionnaire();
            else setType(type);
          }}
        >
          <FontAwesomeIcon
            className="mr-1 text-primary"
            icon={ICON_LOOKUP[type]}
          />
          {I18n.t(`js.composer.editor.${type}.label`)}
        </button>
      ))}
    </div>
  );
}
